import React, { useMemo, useState, useCallback } from "react";
import { PianoKeyboard } from "../piano/PianoKeyboard";
import { ChordCompactStaff } from "./ChordCompactStaff";
import { PianoPracticePanel } from "../piano/PianoPracticePanel";
import type { NoteName } from "../../lib/guitarEngine";
import type { ExtendedChordQuality } from "../../lib/chordEngine";
import type { ExplorerFilter, InversionType } from "../../lib/chordEngine";
import { buildChordStaffEvents } from "../../lib/chordStaffEvents";
import { buildChordKeyboardFingering } from "../../lib/chordKeyboardLayout";
import { getChordDef } from "../../lib/chordEngine";

export interface ChordPianoWorkbenchProps {
  root: NoteName;
  quality: ExtendedChordQuality;
  activeCategory: string;
  inversion: InversionType;
  voicingType: ExplorerFilter["voicingType"];
  onCategoryChange: (id: string) => void;
  onQualityChange: (q: ExtendedChordQuality) => void;
  onKeyClick?: (midi: number) => void;
}

export const ChordPianoWorkbench: React.FC<ChordPianoWorkbenchProps> = ({
  root,
  quality,
  activeCategory,
  inversion,
  voicingType,
  onCategoryChange,
  onQualityChange,
  onKeyClick,
}) => {
  const [activeMidi, setActiveMidi] = useState<number | null>(null);
  const def = getChordDef(quality);

  const staffEvents = useMemo(
    () => buildChordStaffEvents(root, quality, inversion, voicingType),
    [root, quality, inversion, voicingType]
  );

  const fingering = useMemo(
    () => buildChordKeyboardFingering(root, quality, inversion, voicingType),
    [root, quality, inversion, voicingType]
  );

  const highlightMidis = useMemo(() => new Set(fingering.map((f) => f.midi)), [fingering]);

  const chordLabel = `${root} ${def?.label ?? quality}`;
  const degreeLine = def?.degreeNames.join(" · ");

  const handleKeyClick = useCallback(
    (midi: number) => {
      setActiveMidi(midi);
      onKeyClick?.(midi);
    },
    [onKeyClick]
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 w-full gap-2">
      <div className="shrink-0 w-full flex-[1.35] min-h-[220px] flex flex-col justify-center">
        <ChordCompactStaff
          events={staffEvents}
          chordLabel={chordLabel}
          degreeLine={degreeLine}
          activeMidi={activeMidi}
          className="h-full min-h-[220px]"
        />
        <p className="text-[10px] text-slate-500 font-sans mt-1 text-center px-2">
          Cada color es un grado del acorde — así se ve en partitura y en el teclado.
        </p>
      </div>

      <PianoPracticePanel className="flex-1 min-h-0">
        <PianoKeyboard
          fingering={fingering}
          activeMidi={activeMidi}
          highlightMidis={highlightMidis}
          showFingerRings={false}
          showDegreeFill
          dimUnmapped={false}
          embedded
          showHandZones={false}
          onKeyClick={handleKeyClick}
        />
      </PianoPracticePanel>
    </div>
  );
};
